import { DataSource, Label, Snackbar, TableRowDisplayEvent, Toast } from "@mcleod/components";
import { Api, LogManager } from "@mcleod/core";
import { AutogenLayoutMobileCommControlConfigurations } from "./autogen/AutogenLayoutMobileCommControlConfigurations";

const log = LogManager.getLogger("mobilecomm.MobileCommControlConfigurations");

export class MobileCommControlConfigurations extends AutogenLayoutMobileCommControlConfigurations {
    private _parentDataSource: DataSource = null;

    async onLoad() {
        await super.onLoad();

        this.attachmentConfigFile.setAttachmentWithUI = async (file: File) => {
            const fileName = file.name.toLowerCase();

            if (!fileName.endsWith(".config"))
                Snackbar.showWarningSnackbar("The file must be a config file.", { persist: true });
            else {
                this.sendFileToServer(file);
            }
        }

        this.tableScheduledConfig.addRowDisplayListener(event => this.rowDisplayed(event));
    }

    rowDisplayed(event: TableRowDisplayEvent) {
        const row = event.getTableRow();
        const fileNameLabel = row?.findComponentById("labelFileName") as Label;
        if (fileNameLabel) {
            fileNameLabel.tooltipDynamicContent = () => row.data.get("file_contents");
        }
    }

    private sendFileToServer(file: File) {
        const fileName = file.name;

        // Validate file extension
        if (!fileName.toLowerCase().endsWith(".config")) {
            this.showAdhocTooltip("The file must be a config file.", { shaking: true, timeout: 5000 });
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            try {
                const fileContents = reader.result as string;
                this.processConfigFile(fileName, fileContents);
            } catch (error) {
                log.error("Error reading file", error);
                this.showAdhocTooltip("Error reading the file contents.", { shaking: true, timeout: 5000 });
            }
        };

        reader.onerror = (error) => {
            log.error("FileReader error", error);
            this.showAdhocTooltip("Error reading " + fileName, { shaking: true, timeout: 5000 });
        };

        reader.readAsText(file);
    }

    private async processConfigFile(fileName: string, fileContents: string) {
        try {
            const requestBody = {
                mc_control_id: this.parentDataSource?.activeRow.get("id") as string,
                file_name: fileName,
                file_contents: fileContents
            };

            const response = await Api.post("lme/dispatch/mobilecomm/mobile-comm-config-file", requestBody);

            if (response && response?.data[0]?.exceptions && response?.data[0]?.exceptions?.length > 0) {
                // Process exceptions and show warning snackbars
                for (const exception of response?.data[0].exceptions) {
                    const exceptionMessage = exception.message || exception.toString() || "Unknown error occurred";
                    Snackbar.showWarningSnackbar(exceptionMessage, { persist: true });
                }
            } else {
                // Success case - file processed without exceptions
                // Refresh the table data source to requery and display updated data
                await this.tableScheduledConfig.dataSource.search(this.tableScheduledConfig.dataSource.lastSearch);

                // Show success toast message
                Toast.showToast("Configuration file has been uploaded successfully", { backgroundColor: "success" }, { millisUntilDismissal: 5000 });
            }
        } catch (error) {
            log.error("Error processing config file", error);
            this.showAdhocTooltip("Error processing the config file.", { shaking: true, timeout: 5000 });
        }
    }

    public get parentDataSource(): DataSource {
        return this._parentDataSource;
    }

    public set parentDataSource(value: DataSource) {
        this._parentDataSource = value;
    }
}
